/**
 * NIGHT OWL - Neurosymbolic AI Training Visualizer
 *
 * Cinematic 3D visualization of multi-agent neurosymbolic learning
 * - Neural agents with visible layer activations
 * - Symbolic agents with rule graphs
 * - Hybrid agents combining both paradigms
 * - Real-time communication flow visualization
 * - Training progress indicators
 */

import { useRef, useEffect, useMemo, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Line, Sphere, Html } from '@react-three/drei';
import * as THREE from 'three';
import { getMockDataGenerator, Agent, Communication } from '../data/mockTrainingData';
import MetricsDashboard from './MetricsDashboard';

// Color scheme
const COLORS = {
  neural: new THREE.Color('#3b82f6'),
  symbolic: new THREE.Color('#d946ef'),
  hybrid: new THREE.Color('#10b981'),
  communication: {
    knowledge_transfer: '#3b82f6',
    gradient_sync: '#d946ef',
    rule_exchange: '#10b981',
    attention_weights: '#f59e0b',
  },
};

// Agent node with pulsing glow and status indicator
interface AgentNodeProps {
  agent: Agent;
  onClick?: (agent: Agent) => void;
  onHover?: (agent: Agent | null) => void;
}

function AgentNode({ agent, onClick, onHover }: AgentNodeProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  // Pulse animation based on training status
  useFrame((state) => {
    if (meshRef.current && glowRef.current) {
      const time = state.clock.elapsedTime;

      // Training pulse
      if (agent.status === 'training') {
        const pulse = 1 + Math.sin(time * 3) * 0.15;
        meshRef.current.scale.setScalar(pulse);
        glowRef.current.scale.setScalar(pulse * 1.5);
      } else if (agent.status === 'communicating') {
        const pulse = 1 + Math.sin(time * 5) * 0.2;
        meshRef.current.scale.setScalar(pulse);
        glowRef.current.scale.setScalar(pulse * 2);
      } else if (agent.status === 'converging') {
        meshRef.current.scale.setScalar(1);
        glowRef.current.scale.setScalar(1.2);
      }

      // Rotate glow sphere
      glowRef.current.rotation.x = time * 0.5;
      glowRef.current.rotation.y = time * 0.7;
    }
  });

  const color = agent.type === 'neural' ? COLORS.neural :
                agent.type === 'symbolic' ? COLORS.symbolic : COLORS.hybrid;

  return (
    <group position={agent.position}>
      {/* Outer glow */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[0.8, 32, 32]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.15}
          side={THREE.BackSide}
        />
      </mesh>

      {/* Main agent sphere */}
      <mesh
        ref={meshRef}
        onClick={() => onClick?.(agent)}
        onPointerOver={() => {
          setHovered(true);
          onHover?.(agent);
        }}
        onPointerOut={() => {
          setHovered(false);
          onHover?.(null);
        }}
      >
        <sphereGeometry args={[0.5, 32, 32]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={hovered ? 1.0 : 0.5}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Agent label */}
      <Html position={[0, 0.8, 0]} center distanceFactor={15}>
        <div className="text-center pointer-events-none">
          <div className={`text-xs font-mono font-bold ${hovered ? 'text-white' : 'text-gray-400'}`}>
            {agent.name}
          </div>
          {hovered && (
            <div className="mt-1 glass-panel px-2 py-1 text-[10px]">
              <div className="text-blue-400">Acc: {(agent.accuracy * 100).toFixed(1)}%</div>
              <div className="text-purple-400">Loss: {agent.loss.toFixed(3)}</div>
              <div className="text-gray-400">Epoch: {agent.epoch}</div>
            </div>
          )}
        </div>
      </Html>

      {/* Neural layer visualization for neural/hybrid agents */}
      {(agent.type === 'neural' || agent.type === 'hybrid') && hovered && (
        <NeuralLayersViz agent={agent} />
      )}

      {/* Symbolic rules visualization for symbolic/hybrid agents */}
      {(agent.type === 'symbolic' || agent.type === 'hybrid') && hovered && (
        <SymbolicRulesViz agent={agent} />
      )}

      {/* Status indicator ring */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, -0.1, 0]}>
        <ringGeometry args={[0.6, 0.65, 32]} />
        <meshBasicMaterial
          color={
            agent.status === 'training' ? '#3b82f6' :
            agent.status === 'converging' ? '#10b981' :
            agent.status === 'communicating' ? '#d946ef' : '#6b7280'
          }
          transparent
          opacity={0.6}
        />
      </mesh>
    </group>
  );
}

// Neural network layers visualization
function NeuralLayersViz({ agent }: { agent: Agent }) {
  const dataGen = getMockDataGenerator();
  const layers = dataGen.getNeuralLayers(agent.id);

  return (
    <group position={[2, 0, 0]}>
      {layers.map((layer, idx) => {
        const yOffset = (idx - layers.length / 2) * 0.4;
        const size = Math.log(layer.neurons) / 20;
        const activation = layer.activation;

        return (
          <group key={layer.id} position={[0, yOffset, 0]}>
            {/* Layer node */}
            <mesh>
              <boxGeometry args={[size, 0.2, 0.2]} />
              <meshStandardMaterial
                color={new THREE.Color().setHSL(0.6, activation, 0.5)}
                emissive={new THREE.Color().setHSL(0.6, activation, 0.3)}
                emissiveIntensity={activation}
              />
            </mesh>

            {/* Connection to next layer */}
            {idx < layers.length - 1 && (
              <Line
                points={[[0, 0, 0], [0, 0.4, 0]]}
                color="#3b82f6"
                lineWidth={1}
                transparent
                opacity={0.3}
              />
            )}
          </group>
        );
      })}
    </group>
  );
}

// Symbolic rules graph visualization
function SymbolicRulesViz({ agent }: { agent: Agent }) {
  const dataGen = getMockDataGenerator();
  const rules = dataGen.getSymbolicRules(agent.id).slice(0, 8);  // Show top 8 rules

  return (
    <group position={[-2, 0, 0]}>
      {rules.map((rule, idx) => {
        const angle = (idx / rules.length) * Math.PI * 2;
        const radius = 1.2;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        const confidence = rule.confidence;

        return (
          <group key={rule.id} position={[x, y, 0]}>
            {/* Rule node */}
            <mesh>
              <sphereGeometry args={[0.1, 16, 16]} />
              <meshStandardMaterial
                color={new THREE.Color().setHSL(0.8, confidence, 0.5)}
                emissive={new THREE.Color().setHSL(0.8, confidence, 0.3)}
                emissiveIntensity={confidence}
              />
            </mesh>

            {/* Connection to center */}
            <Line
              points={[[0, 0, 0], [-x, -y, 0]]}
              color="#d946ef"
              lineWidth={1}
              transparent
              opacity={confidence * 0.5}
            />
          </group>
        );
      })}
    </group>
  );
}

// Communication beam between agents
interface CommBeamProps {
  comm: Communication;
  agents: Agent[];
}

function CommBeam({ comm, agents }: CommBeamProps) {
  const lineRef = useRef<any>(null);
  const [age, setAge] = useState(0);

  const fromAgent = agents.find(a => a.id === comm.from);
  const toAgent = agents.find(a => a.id === comm.to);

  useFrame((_state, delta) => {
    setAge(prev => prev + delta);
  });

  if (!fromAgent || !toAgent || age > 1) return null;

  // Bezier curve for smooth arc
  const start = new THREE.Vector3(...fromAgent.position);
  const end = new THREE.Vector3(...toAgent.position);
  const midpoint = new THREE.Vector3().lerpVectors(start, end, 0.5);

  // Arc upward slightly
  const up = new THREE.Vector3(0, 1, 0);
  const direction = new THREE.Vector3().subVectors(end, start);
  const perpendicular = new THREE.Vector3().crossVectors(direction, up).normalize();
  const offset = perpendicular.multiplyScalar(direction.length() * 0.15);
  const control = midpoint.clone().add(offset);

  const curve = new THREE.QuadraticBezierCurve3(start, control, end);
  const points = curve.getPoints(50);

  // Animated opacity based on age
  const opacity = Math.max(0, 1 - age);

  const color = COLORS.communication[comm.type];

  return (
    <>
      <Line
        ref={lineRef}
        points={points}
        color={color}
        lineWidth={2}
        transparent
        opacity={opacity * 0.8}
      />

      {/* Moving particle along the beam */}
      <Sphere
        position={curve.getPoint(Math.min(age * 1.5, 1)).toArray()}
        args={[0.15, 16, 16]}
      >
        <meshBasicMaterial color={color} />
      </Sphere>
    </>
  );
}

// Background particle field
function ParticleField() {
  const particlesRef = useRef<THREE.Points>(null);

  const particles = useMemo(() => {
    const count = 500;
    const positions = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 80;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 80;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 80;
    }

    return positions;
  }, []);

  useFrame((state) => {
    if (particlesRef.current) {
      particlesRef.current.rotation.y = state.clock.elapsedTime * 0.02;
    }
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[particles, 3]}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        color="#4b5563"
        transparent
        opacity={0.4}
        sizeAttenuation
      />
    </points>
  );
}

// Main scene component
function TrainingScene() {
  const dataGen = getMockDataGenerator();
  const [agents, setAgents] = useState<Agent[]>(dataGen.getAgents());
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [hoveredAgent, setHoveredAgent] = useState<Agent | null>(null);

  // Update simulation every 110ms (10% slower tempo)
  useEffect(() => {
    const interval = setInterval(() => {
      dataGen.tick();
      setAgents([...dataGen.getAgents()]);
      setCommunications([...dataGen.getCommunications()]);
    }, 110);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Ambient lighting */}
      <ambientLight intensity={0.3} />

      {/* Directional lights */}
      <directionalLight position={[10, 10, 5]} intensity={0.5} />
      <directionalLight position={[-10, -10, -5]} intensity={0.3} color="#3b82f6" />

      {/* Point lights for dramatic effect */}
      <pointLight position={[0, 0, 0]} intensity={1} color="#d946ef" />

      {/* Background particles */}
      <ParticleField />

      {/* Agent nodes */}
      {agents.map(agent => (
        <AgentNode
          key={agent.id}
          agent={agent}
          onClick={setSelectedAgent}
          onHover={setHoveredAgent}
        />
      ))}

      {/* Communication beams */}
      {communications.map(comm => (
        <CommBeam key={comm.id} comm={comm} agents={agents} />
      ))}

      {/* Central knowledge graph visualization */}
      <mesh position={[0, 0, 0]}>
        <torusGeometry args={[10, 0.05, 16, 100]} />
        <meshBasicMaterial color="#1f2937" transparent opacity={0.2} />
      </mesh>

      {/* Orbit controls */}
      <OrbitControls
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={10}
        maxDistance={60}
        autoRotate={!selectedAgent && !hoveredAgent}
        autoRotateSpeed={0.3}
      />
    </>
  );
}

// Main component
export default function NeurosymbolicVisualizer() {
  return (
    <div className="w-full h-full bg-gray-950 relative">
      <Canvas
        camera={{ position: [25, 15, 25], fov: 60 }}
        gl={{ antialias: true, alpha: true }}
        style={{ background: 'radial-gradient(circle at 50% 50%, #0f172a 0%, #030712 100%)' }}
      >
        <TrainingScene />
      </Canvas>

      {/* Compact metrics overlay at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-2">
        <MetricsDashboard />
      </div>
    </div>
  );
}
